import { BatchType } from 'features/autocheck/types'
import { ICSVImportPostResponse, IFileUploadError } from 'features/claims/types'
import { ChangeEvent, useState, useEffect } from 'react'
import styled from 'styled-components'

import { BatchFileUploadDetails } from '../internal/BatchFileUploadDetails'
import { BatchUploadButton } from '../internal/BatchUploadButton'
import { FileDragDrop } from '../internal/FileDragDrop'
import { useCustomerPreference } from 'features/settings/settingsHooks'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { SettingsRequestTypeEnum } from 'features/settings/types/settingsEnum'
import { useAppSelector } from 'appState/store'
import { Message } from '@outbound-ai/ursa'

interface IProps {
  setFileName: (value: string | null) => void
  batchType: BatchType
  onSetClaimData: (data: ICSVImportPostResponse | null) => void
  setFileError: (error: IFileUploadError | null) => void
  repeatCalls: (value: boolean) => void
  eobSelected: (value: boolean) => void
  isPocCustomer: boolean | undefined
}

export const ClaimFileUpload = ({
  batchType,
  onSetClaimData,
  setFileError,
  setFileName,
  repeatCalls,
  eobSelected,
  isPocCustomer,
}: IProps) => {
  const { eobEnabled, eobFaxNumber } = useAppSelector((state) => state.auth)
  const [file, setFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // const [requestEob, setRequestEob] = useState(false)
  const [isEobEnabled, setEobEnabled] = useState(false)
  const [responseData, setResponseData] = useState<ICSVImportPostResponse | null>(null)
  const [repeatedCalls, setRepeatedCalls] = useState(false)
  const [tenantAutoCheck, setTenantAutoCheck] = useState({
    AutoCheck: SettingsRequestTypeEnum.everytime,
    EOB: SettingsRequestTypeEnum.everytime,
    AutoCheckCalls: SettingsRequestTypeEnum.always,
  })

  const { data: tenantAutoCheckData } = useCustomerPreference(UserPreferencesKeysEnum.AutoCheckPreferences)

  useEffect(() => {
    if (tenantAutoCheckData?.value) {
      const autoCheck = JSON.parse(tenantAutoCheckData?.value)
      setTenantAutoCheck(autoCheck)
    }
  }, [tenantAutoCheckData])

  useEffect(() => {
    repeatCalls(false)
    if (eobEnabled && eobFaxNumber !== '' && batchType === BatchType.Claim_Autocheck) {
      setEobEnabled(true)
    } else {
      setEobEnabled(false)
    }
  }, [])

  const setClaimResponseData = (data: ICSVImportPostResponse | null) => {
    setResponseData(data)
    onSetClaimData(data)
  }

  const handleDroppedFile = (droppedFile: File) => {
    setFileName(droppedFile.name)
    setFile(droppedFile)
  }

  const handleRemoveFile = () => {
    setFileName(null)
    setFile(null)
  }

  const handleRequestEob = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      eobSelected(e.target.checked)
    } else {
      eobSelected(false)
    }
  }

  const handleRepeatCall = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      repeatCalls(e.target.checked)
    } else {
      repeatCalls(false)
    }

    setRepeatedCalls(e.target.checked)
  }

  const isUploadEobAndRepeatCallAvailable =
    (isEobEnabled ||
      (tenantAutoCheck.AutoCheckCalls === SettingsRequestTypeEnum.everytime && batchType !== BatchType.Claim_Import)) ??
    true

  return (
    <Div data={responseData} file={file}>
      <div className="content">
        {!file ? (
          <FileDragDrop onFile={handleDroppedFile} />
        ) : (
          <BatchFileUploadDetails file={file} data={responseData} onRemoveFile={handleRemoveFile} />
        )}
        {isUploadEobAndRepeatCallAvailable && (
          <div className="content-inner">
            <div className="content-radio-container">
              {isEobEnabled && (
                <div className="checkbox-container">
                  <label className="checkbox-label">
                    <input
                      name="eob-checkbox"
                      className="checkbox"
                      type="checkbox"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleRequestEob(e)}
                    />
                    <div className="sf-custom-checkbox-input mx-auto"></div>
                  </label>
                  <p>Request EOBs</p>
                </div>
              )}

              {tenantAutoCheck.AutoCheckCalls === SettingsRequestTypeEnum.everytime &&
                batchType !== BatchType.Claim_Import && (
                  <div className="checkbox-container">
                    <label className="checkbox-label">
                      <input
                        name="repeat-call"
                        className="checkbox"
                        type="checkbox"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleRepeatCall(e)}
                      />
                      <div className="sf-custom-checkbox-input mx-auto"></div>
                    </label>
                    <p>Repeat calls on previously AutoChecked claims</p>
                  </div>
                )}
            </div>
          </div>
        )}

        {isPocCustomer && batchType != BatchType.Claim_Import && (
          <div className="limit-message">
            <Message
              type="info"
              message="If the number of claims exceeds the limit allowed per batch during your trial period the remaining claims will be uploaded for Import Only."
            />
          </div>
        )}

        <BatchUploadButton
          file={file}
          // data={responseData}
          loading={isLoading}
          onIsLoading={(status: boolean) => setIsLoading(status)}
          onSetData={(data: ICSVImportPostResponse | null) => setClaimResponseData(data)}
          onSetError={(error: IFileUploadError | null) => {
            // setError(error)
            setFileError(error)
          }}
          onRemoveFile={handleRemoveFile}
          batchType={batchType}
          repeatedCalls={repeatedCalls}
        />
      </div>
    </Div>
  )
}

const Div = styled.div<StyledProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 2rem;
  padding-top: 0;
  .breadcrumb-container {
    .right-section {
      border: none;
    }
  }

  h3 {
    font-size: 24px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    margin-top: -2rem;
    color: #31457b;
    margin-bottom: -2rem;
    justify-content: left;
    align-items: left;
  }

  .content {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    .content-inner {
      width: 100%;
      background-color: ${({ theme }) => theme.colors.grey.lightest};
      border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
      border-radius: 9px;
      border-top-left-radius: 0;
      border-top: 0;
      border-top-right-radius: 0;
      margin-top: -3px;
      .content-radio-container {
        display: flex;
        align-items: center;
        justify-content: space-evenly;
        padding: 16px;
        padding-bottom: 14px;
        .checkbox-container {
          display: flex;
          p {
            font-size: 12px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.33;
            letter-spacing: normal;
            text-align: left;
            color: ${({ theme }) => theme.colors.grey.dark};
          }
        }

        .checkbox,
        .checkbox {
          width: 16px;
          height: 16px;
          margin-right: 12px;
          cursor: pointer;
        }
      }
    }

    .limit-message {
      margin-top: 1.5rem;
    }

    .btn-validate {
      width: 100%;
      height: 3rem;
      margin-top: 1.5rem;
      span {
        margin-left: 0.5rem;
      }
    }
  }
`

interface StyledProps {
  file: File | null
  data: ICSVImportPostResponse | null
  // error: IFileUploadError | null
}
